import type { PrototypeMeta } from './discoverPrototypes';

const DISPLAY_TIME_ZONE = 'Europe/Paris';
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/i;

function utcTimestamp(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, 0);
  return date.getTime();
}

function isValidDate(year: number, month: number, day: number): boolean {
  const date = new Date(utcTimestamp(year, month, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
}

function parisOffsetAt(timestamp: number): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(timestamp));
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  const parisAsUtc = utcTimestamp(
    Number(values.year),
    Number(values.month),
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second),
  );
  return parisAsUtc - timestamp;
}

export function parseUpdatedAt(value?: string): number | null {
  if (!value) return null;

  const dateOnly = value.match(DATE_ONLY_PATTERN);
  if (dateOnly) {
    const [, yearText, monthText, dayText] = dateOnly;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!isValidDate(year, month, day)) return null;

    const parisDateAsUtc = utcTimestamp(year, month, day);
    return parisDateAsUtc - parisOffsetAt(parisDateAsUtc);
  }

  const dateTime = value.match(DATE_TIME_PATTERN);
  if (!dateTime) return null;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText = '0'] = dateTime;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (
    !isValidDate(year, month, day) ||
    Number(hourText) > 23 ||
    Number(minuteText) > 59 ||
    Number(secondText) > 59
  ) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function formatUpdatedAt(value?: string): string {
  const timestamp = parseUpdatedAt(value);
  if (timestamp === null) return 'Non renseignée';

  const isDateTime = DATE_TIME_PATTERN.test(value ?? '');
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'long',
    ...(isDateTime ? { timeStyle: 'short' as const } : {}),
    timeZone: DISPLAY_TIME_ZONE,
  }).format(timestamp);
}

export function compareUpdatedAtDescending(a?: string, b?: string): number {
  const timestampA = parseUpdatedAt(a);
  const timestampB = parseUpdatedAt(b);
  if (timestampA === null) return timestampB === null ? 0 : 1;
  if (timestampB === null) return -1;
  return timestampB - timestampA;
}

export function comparePrototypesByUpdatedAt(
  a: { meta: PrototypeMeta },
  b: { meta: PrototypeMeta },
): number {
  return compareUpdatedAtDescending(a.meta.updatedAt, b.meta.updatedAt) ||
    a.meta.title.localeCompare(b.meta.title, 'fr');
}
